import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  InboxNotificationSchema,
  ReadAllResultSchema,
  type InboxNotificationDto,
  type MeResponse,
} from '@taqon/contracts';
import { postJson } from '../../shared/api/client';
import { recordPath } from '../../shared/records/routes';
import { hasCapability } from '../../shared/session/session';

export const INBOX = '/api/v1/notifications';
/** Every inbox query shares this prefix, so a change refreshes the bell and the page together. */
export const INBOX_KEY = ['notifications', 'inbox'] as const;

/** Marks one notification read, or all of them, and refreshes every inbox view. */
export function useInboxActions(csrfToken: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: INBOX_KEY });
  const markRead = useMutation({
    mutationFn: (id: string) =>
      postJson(`${INBOX}/${id}/read`, {}, InboxNotificationSchema, csrfToken),
    onSuccess: refresh,
  });
  const markAllRead = useMutation({
    mutationFn: () => postJson(`${INBOX}/read-all`, {}, ReadAllResultSchema, csrfToken),
    onSuccess: refresh,
  });
  return { markRead, markAllRead };
}

/**
 * The subject record's page, when there is one and this session may view its type. The API still
 * decides whether the particular record may be seen.
 */
export function subjectPath(
  me: MeResponse | null | undefined,
  notification: InboxNotificationDto,
): string | null {
  if (!hasCapability(me, `${notification.subjectRecordType}:view`)) return null;
  return recordPath(notification.subjectRecordType, notification.subjectRecordId);
}

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** "5 minutes ago", "yesterday"; "just now" under a minute. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const seconds = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  const format = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
  }
  return 'just now';
}

/** The absolute time, for a tooltip or a screen reader. */
export function absoluteTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  }).format(new Date(iso));
}
